import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cx } from "../internal/cx.js";

const policyIdCardVariants = cva("cw-v2-policy-id-card", {
  variants: {
    variant: {
      default: "cw-v2-policy-id-card--default",
    },
    size: {
      md: "cw-v2-policy-id-card--md",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

export interface PolicyIdCardMember {
  /** Stable member id. */
  id: string;
  /** Covered member name. */
  name: React.ReactNode;
  /** Relationship label rendered in parentheses when not primary. */
  relationship?: React.ReactNode;
  /** Marks this member as the primary covered member. */
  isPrimary?: boolean;
}

export interface PolicyIdCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof policyIdCardVariants> {
  /** Plan/product name rendered as the card heading. */
  title: React.ReactNode;
  /** Carrier name rendered under the plan name. */
  carrierName?: React.ReactNode;
  /** Custom carrier logo slot. Takes precedence over `carrierLogoSrc`. */
  carrierLogo?: React.ReactNode;
  /** Carrier logo image source. */
  carrierLogoSrc?: string;
  /** Accessible logo alt text. Defaults to carrier name when it is a string. */
  carrierLogoAlt?: string;
  /** Primary member display value. */
  primaryMember?: React.ReactNode;
  /** Policyholder display value. */
  policyholder?: React.ReactNode;
  /** Policy/member id display value. */
  policyNumber?: React.ReactNode;
  /** Effective date display value. */
  effectiveDate?: React.ReactNode;
  /** Covered members list. */
  coveredMembers?: PolicyIdCardMember[];
}

/**
 * Benefits Wallet policy ID card for dental, vision, and similar products.
 * It is display-only: callers pass preformatted policy and member values.
 */
export const PolicyIdCard = React.forwardRef<HTMLDivElement, PolicyIdCardProps>(
  function PolicyIdCard(
    {
      carrierLogo,
      carrierLogoAlt,
      carrierLogoSrc,
      carrierName,
      className,
      coveredMembers = [],
      effectiveDate,
      policyholder,
      policyNumber,
      primaryMember,
      size,
      title,
      variant,
      ...props
    },
    ref,
  ) {
    const logo =
      carrierLogo ??
      (carrierLogoSrc ? (
        <img
          alt={carrierLogoAlt ?? (typeof carrierName === "string" ? carrierName : "")}
          className="cw-v2-policy-id-card__logo-image"
          onError={(event) => {
            event.currentTarget.hidden = true;
          }}
          src={carrierLogoSrc}
        />
      ) : null);

    return (
      <div ref={ref} className={cx(policyIdCardVariants({ variant, size }), className)} {...props}>
        <div className="cw-v2-policy-id-card__header">
          <div className="cw-v2-policy-id-card__title-group">
            <h3 className="cw-v2-policy-id-card__title">{title}</h3>
            {carrierName ? <p className="cw-v2-policy-id-card__carrier">{carrierName}</p> : null}
          </div>
          {logo ? <div className="cw-v2-policy-id-card__logo">{logo}</div> : null}
        </div>

        <div className="cw-v2-policy-id-card__details">
          <div className="cw-v2-policy-id-card__column">
            <PolicyIdCardField
              label="Primary Member"
              preserveCase
              value={primaryMember ?? "N/A"}
              strong
            />
            <PolicyIdCardField label="Policy Number" value={policyNumber ?? "N/A"} />
            <PolicyIdCardField label="Effective Date" value={effectiveDate ?? "N/A"} />
          </div>
          <div className="cw-v2-policy-id-card__column">
            <PolicyIdCardField label="Policyholder" preserveCase value={policyholder ?? "N/A"} />
            <div className="cw-v2-policy-id-card__field">
              <div className="cw-v2-policy-id-card__label">All Covered Members</div>
              <div className="cw-v2-policy-id-card__members">
                {coveredMembers.length > 0 ? (
                  coveredMembers.map((member) => (
                    <div
                      className={cx(
                        "cw-v2-policy-id-card__member",
                        "cw-v2-policy-id-card__member--preserve-case",
                        member.isPrimary && "cw-v2-policy-id-card__member--primary",
                      )}
                      key={member.id}
                    >
                      <span className="cw-v2-policy-id-card__member-name">{member.name}</span>
                      {member.isPrimary ? (
                        <span className="cw-v2-policy-id-card__member-relationship">(Primary)</span>
                      ) : member.relationship ? (
                        <span className="cw-v2-policy-id-card__member-relationship">
                          ({member.relationship})
                        </span>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="cw-v2-policy-id-card__value">N/A</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
PolicyIdCard.displayName = "PolicyIdCard";

interface PolicyIdCardFieldProps {
  label: React.ReactNode;
  value: React.ReactNode;
  preserveCase?: boolean;
  strong?: boolean;
}

function PolicyIdCardField({ label, preserveCase, strong, value }: PolicyIdCardFieldProps) {
  return (
    <div className="cw-v2-policy-id-card__field">
      <div className="cw-v2-policy-id-card__label">{label}</div>
      <div
        className={cx(
          "cw-v2-policy-id-card__value",
          preserveCase && "cw-v2-policy-id-card__value--preserve-case",
          strong && "cw-v2-policy-id-card__value--strong",
        )}
      >
        {value}
      </div>
    </div>
  );
}

export const V2PolicyIdCard = PolicyIdCard;
export type V2PolicyIdCardProps = PolicyIdCardProps;
export type V2PolicyIdCardMember = PolicyIdCardMember;
